import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/glam/Header";
import { Footer } from "@/components/glam/Footer";
import { EMAIL, PHONE_DISPLAY, PHONE_HREF, MAPS_URL } from "@/components/glam/data";

const LAST_UPDATED = "August 19, 2026";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
  head: () => ({
    meta: [
      { title: "Privacy Policy | The Glam Room" },
      {
        name: "description",
        content:
          "Read how The Glam Room Hair Salon collects, uses, and protects your personal information.",
      },
      { property: "og:title", content: "Privacy Policy | The Glam Room" },
      {
        property: "og:description",
        content:
          "Read how The Glam Room Hair Salon collects, uses, and protects your personal information.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://theglamroom1111.com/privacy" },
    ],
    links: [{ rel: "canonical", href: "https://theglamroom1111.com/privacy" }],
  }),
});

function PrivacyPage() {
  return (
    <div className="bg-background">
      <Header solid />
      <main id="main-content" tabIndex={-1} className="surface-cream grain">
        <article className="mx-auto max-w-3xl px-5 pt-32 pb-24 sm:pt-36 sm:pb-32 lg:px-8">
          <p className="eyebrow">Legal</p>
          <h1 className="mt-4 text-4xl leading-tight sm:text-5xl">Privacy Policy</h1>
          <p className="mt-3 text-sm tracking-wide text-muted-foreground">
            Last Updated: {LAST_UPDATED}
          </p>

          <div className="mt-10 space-y-6 text-base leading-relaxed text-muted-foreground">
            <p>
              This Privacy Policy describes how The Glam Room Hair Salon ("we," "us," or "our")
              collects, uses, and shares information from individuals who visit our website, use our
              services, or otherwise interact with us ("Consumers"). We are committed to protecting
              your privacy and ensuring the security of your Personally Identifiable Information
              (PII). Please read this Privacy Policy carefully to understand how we handle your
              information.
            </p>
            <p>
              By accessing or using our website or services, you consent to the practices described
              in this Privacy Policy.
            </p>
          </div>

          <section className="mt-12">
            <h2 className="text-3xl">1. Information We Collect</h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              We collect the following types of information:
            </p>
            <h3 className="mt-8 text-2xl">Personal Information</h3>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              When you visit our website or use our services, we may collect personal information
              that you provide to us voluntarily. This may include your name, email address, phone
              number, and any other information you choose to provide.
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              We may use your Personal Data to contact you with newsletters, marketing, and/or
              promotional materials that may be of interest to you. By entering your phone number,
              you are agreeing to receive text messages from us. Message &amp; data rates may apply.
              Message frequency may vary. Reply HELP for more information. You can reply STOP or
              UNSUBSCRIBE to opt-out.
            </p>
            <h3 className="mt-8 text-2xl">Automatically Collected Information</h3>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              We may also collect certain information automatically when you use our website, such
              as your IP address, browser type, device type, and the pages you visit.
            </p>
            <h3 className="mt-8 text-2xl">Cookies</h3>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              We may use cookies and similar technologies to collect information about your browsing
              behavior. You can learn more about our use of cookies in our Cookie Policy.
            </p>
          </section>

          <section className="mt-12">
            <h2 className="text-3xl">2. How We Use Your Information</h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              We use the information we collect for the following purposes:
            </p>
            <h3 className="mt-8 text-2xl">Providing Services</h3>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              We may use your information to provide you with our salon services and to respond to
              your inquiries.
            </p>
            <h3 className="mt-8 text-2xl">Communication</h3>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              We may use your contact information to send you important updates and promotional
              materials. You can opt out of receiving promotional communications at any time.
            </p>
            <h3 className="mt-8 text-2xl">Website Improvement</h3>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              We may use your information to analyze and improve our website's functionality,
              content, and user experience.
            </p>
          </section>

          <section className="mt-12">
            <h2 className="text-3xl">3. Sharing Your Information</h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              We do not share or sell your Personally Identifiable Information to third parties for
              marketing purposes. No mobile information will be shared with third parties/affiliates
              for marketing/promotional purposes. However, we may share your information in the
              following circumstances:
            </p>
            <h3 className="mt-8 text-2xl">Business Operations</h3>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              We may share your information with third-party service providers who assist us in
              performing essential business functions, such as payment processing, customer support,
              and website maintenance.
            </p>
            <h3 className="mt-8 text-2xl">Compliance with Legal Requirements</h3>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              We may disclose your information when required by law or to comply with legal
              processes.
            </p>
            <h3 className="mt-8 text-2xl">Sale or Merger</h3>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              In the event of a sale, merger, or acquisition of our business, we may transfer your
              information to the acquiring company, but your data will continue to be protected in
              accordance with this Privacy Policy.
            </p>
          </section>

          <section className="mt-12">
            <h2 className="text-3xl">4. Your Choices and Rights</h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              You have the following rights regarding your information:
            </p>
            <h3 className="mt-8 text-2xl">Access</h3>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              You can request access to the personal information we hold about you.
            </p>
            <h3 className="mt-8 text-2xl">Rectification</h3>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              You can request corrections to inaccuracies in your personal information.
            </p>
            <h3 className="mt-8 text-2xl">Deletion</h3>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              You can request the deletion of your personal information, subject to legal
              obligations.
            </p>
            <h3 className="mt-8 text-2xl">Data Portability</h3>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              You can request a copy of your personal information in a structured, machine-readable
              format.
            </p>
          </section>

          <section className="mt-12">
            <h2 className="text-3xl">5. Changes to this Privacy Policy</h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              We may update this Privacy Policy from time to time to reflect changes in our
              practices or for other operational, legal, or regulatory reasons. We will post the
              updated Privacy Policy on our website, and the "Last Updated" date at the top of this
              page will be revised accordingly. We encourage you to review this Privacy Policy
              periodically to stay informed about how we are protecting your information.
            </p>
          </section>

          <section className="mt-12">
            <h2 className="text-3xl">6. Contact Us</h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              If you have any questions or concerns about this Privacy Policy or our data practices,
              please contact our corporate office at:
            </p>
            <address className="mt-6 not-italic text-base leading-relaxed text-foreground">
              <p className="font-display text-2xl">The Glam Room</p>
              <p className="mt-3 text-muted-foreground">
                <a
                  href={MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline-offset-4 hover:underline"
                >
                  8961 Hypoluxo Rd, Lake Worth, FL 33467
                </a>
              </p>
              <p className="mt-2 text-muted-foreground">
                Telephone:{" "}
                <a href={PHONE_HREF} className="underline-offset-4 hover:underline">
                  +1 {PHONE_DISPLAY}
                </a>
              </p>
              <p className="mt-2 text-muted-foreground">
                Email:{" "}
                <a href={`mailto:${EMAIL}`} className="underline-offset-4 hover:underline">
                  {EMAIL}
                </a>
              </p>
            </address>
          </section>

          <p className="mt-14 text-sm text-muted-foreground">
            <Link to="/" className="text-gold-deep underline-offset-4 hover:underline">
              Back to The Glam Room
            </Link>
          </p>
        </article>
      </main>
      <Footer compact />
    </div>
  );
}
